/* ============================================================== */
/*                                                                */
/*   historyModelV4  ·  domain ของหน้า Patient History Viewer      */
/*   (HIS V4 · อ่านอย่างเดียว · ดูประวัติย้อนหลังทุก encounter)     */
/*                                                                */
/*   pure model: ไม่มี issara-sdk / react imports (test Node ได้)    */
/*                                                                */
/*   แหล่งที่มาของพฤติกรรม (HIS V3 ของจริง):                         */
/*     /apis/DPO/encounter-history/  (EncounterHistoryList_apps_DPO) */
/*        params: patient, encounter, start, end, doctor, division,  */
/*                selected_tab                                       */
/*     HISV3/PHV/CardPHVWrapper.tsx:961-1001  (params ที่ V3 ส่งจริง) */
/*     apps/PHV/CardPHV.tsx:63-90  (tab เดียวกันกับแบบ 4d)           */
/*     apps/PHV/CardSecretEncounter + /apis/core/secret-encounter/   */
/*   แบบของ UI: docs/design/PatientHistory.html (4d · PHV)           */
/*                                                                */
/* ============================================================== */

import { medCertsFromLogs, medCertTypesFromApi } from "./documentModelV4";
import {
  allergyItemsFromApi,
  asNumber,
  asText,
  decodeHtml,
  diagnosisFromApi,
  diagnosisLabel,
  galleryFromApi,
  itemsOf,
  nurseNotesFromApi,
  nurseNoteText,
  problemItemsFromApi,
  subjectiveFromEmr,
  triageFromApi,
  vitalLabel,
  vitalsFromApi,
  type Dict,
} from "./examModelV4";
import { imagingResultsFromApi, labResultsFromApi, stripHtml } from "./resultModelV4";
import { consultNotesFromApi } from "./consultModelV4";

/* ============================================================== */
/*   SECTION 1 · TYPE                                              */
/* ============================================================== */

export type HistoryIds = {
  patientId?: number;
  encounterId?: number;
  emrId?: number;
  divisionId?: number;
  doctorId?: number;
};

export const HistoryIdsInitial: HistoryIds = {};

/** ช่วงเวลาของแบบ 4d: 3 เดือน / 1 ปี / ทั้งหมด */
export type HistoryPeriodKey = "3m" | "1y" | "all";

export const HISTORY_PERIODS: { key: HistoryPeriodKey; label: string }[] = [
  { key: "3m", label: "3 เดือน" },
  { key: "1y", label: "1 ปี" },
  { key: "all", label: "ทั้งหมด" },
];

export type HistoryFilter = {
  period: HistoryPeriodKey;
  /** คำค้น "Dx / ยา / หัตถการ / แพทย์" (แบบ 4d ใช้คำเดียวกับ placeholder) */
  search: string;
  /** ตัวกรองเพิ่มเติม: ประเภท */
  opd: boolean;
  ipd: boolean;
  /** วันที่รับบริการ / ถึง รูปแบบ วว/ดด/ปปปป (พ.ศ.) ตามแบบ 4d */
  from: string;
  to: string;
  /** แผนก (label หรือ id) — "" = ทั้งหมด */
  division: string;
  /** แพทย์ (ชื่ອต้นตอน) — "" = ทุกแพทย์ */
  doctor: string;
};

export const HistoryFilterInitial: HistoryFilter = {
  period: "3m",
  search: "",
  opd: true,
  ipd: true,
  from: "",
  to: "",
  division: "",
  doctor: "",
};

export type HistoryEncounterType = "OPD" | "IPD" | "";

/** แถว encounter จาก /apis/DPO/encounter-history/ */
export type HistoryEncounterRow = {
  id: number;
  /** เลข encounter ที่แสดงใน HN ของหน่วยตรวจ เช่น "29/1" */
  number: string;
  hn: string;
  /** ชื่อผู้ป่วยจาก encounter-history (ใช้เมื่อหน้าจอยังไม่มีข้อมูลผู้ป่วย) */
  patientName?: string;
  /** "15/09/2569 10:12" (พ.ศ. ตามแบบ 4d) */
  dateText: string;
  /** 25690915 ใช้เทียบลำดับ/กรองด้วยวันที่ (null เมื่อ parse ไม่ได้) */
  dateNum: number | null;
  /** yyyy-mm-ddTHH:mm:ss ที่ backend ส่ง (ใช้ใน payload/ลิงก์) */
  dateIso: string;
  /** วัน checkout "15/09/2569 12:05" */
  checkoutText: string;
  type: HistoryEncounterType;
  divisionName: string;
  doctorName: string;
  /** วินิจฉัยเดียว/หลายตัวรวมกันเป็นข้อความ (principal + secondary) */
  diagnosisText: string;
  packageUsed: boolean;
  emrId: number | null;
  /** backend ตอบ is_secret (encounter-history) = ประวัตินี้ถูกปกปิดจริง */
  isSecret: boolean;
  /** ค่าที่ใช้กับช่องค้นหา (dx + แพทย์ + ยา + หัตถการ + แลป) */
  searchBlob: string;
};

export type HistoryRow = { key: string; value: string; center?: boolean };

export type HistoryLabRow = { name: string; value: string; unit: string; refValue: string; flag: string };

export type HistoryImagingRow = { name: string; result: string; impression: string };

export type HistoryMedRow = { name: string; howToUse: string; quantity: string; status?: string };

export type HistoryProcRow = { name: string; result: string };

export type HistoryPicture = {
  id: number;
  label: string;
  caption: string;
  meta: string;
  mark: string;
  url: string;
};

export type HistoryFileRow = {
  id: number;
  name: string;
  meta: string;
  url?: string;
  certificate?: boolean;
  /** ใบรับรองจากเมนูใบรับรองของ V3/V4 ซึ่งเก็บใน FormData */
  formCertificate?: boolean;
};

export type HistoryDetail = {
  encounter: HistoryEncounterRow;
  /** ตาราง EMR Summary (kv) */
  summaryRows: HistoryRow[];
  /** ตารางข้อมูลคัดกรอง (kv) */
  screeningRows: HistoryRow[];
  labs: HistoryLabRow[];
  imaging: HistoryImagingRow[];
  meds: HistoryMedRow[];
  administration?: HistoryMedRow[];
  procs: HistoryProcRow[];
  nurseNotes: string[];
  /**
   * บันทึกทีมสหสาขา (Multidisciplinary) ของ Encounter นี้
   * issue 86634 ข้อ 4: ข้อมูลทั้งหมดผูกกับ Encounter และ HN ดูย้อนหลังได้ใน Patient History Viewer
   * การ์ดจะแสดงหัวข้อนี้เมื่อมีแถวจริงเท่านั้น (design bundle 4d ไม่มีหัวข้อนี้)
   */
  consult: HistoryConsultRow[];
  pictures: HistoryPicture[];
  files: HistoryFileRow[];
  certificates?: HistoryFileRow[];
  /** error ย่อยของแต่ละส่วน (null = ส่วนนั้นโหลดไม่สำเร็จ แต่ส่วนอื่นยังแสดง) */
  partialErrors: string[];
  noEmr?: boolean;
};

export type HistoryConsultRow = {
  date: string;
  endDate: string;
  problem: string;
  plan: string;
  goal: string;
  practitioner: string;
  discipline: string;
};

export type HistorySectionKind = "rows" | "table" | "pics" | "files";

export type HistorySection = {
  notice?: string;
  id: string;
  key: string;
  title: string;
  kind: HistorySectionKind;
  grid: string;
  rows: HistoryRow[];
  columns: string[];
  /** แต่ละแถวของตาราง (cells ตรงจำนวนกับ columns, flag ใช้ตั้งสี cell "ผล") */
  lines: { cells: string[]; flag: string }[];
  pictures: HistoryPicture[];
  files: HistoryFileRow[];
  emptyText: string;
};

export type HistoryV4Slice = {
  /** framework เขียนค่านี้ชั่วคราวก่อนที่ Action จะเขียน slice จริง (เหมือน ExamV4Slice) */
  sequenceIndex: string | null;
  ids: HistoryIds;
  filter: HistoryFilter;
  encounters: HistoryEncounterRow[];
  total: number;
  offset?: number;
  nextOffset?: number | null;
  detailLoading?: boolean;
  /** encounter ที่เลือกอยู่ (null = ใช้รายการแรกตามแบบ 4d) */
  selectedId: number | null;
  /** id ที่โหลดรายละเอียดแล้ว (กันโหลดซ้ำตอน render) */
  detailFor: number | null;
  detail: HistoryDetail | null;
  /** ข้อผิดพลาดระดับ list (null = ไม่มี) — เก็บไว้ใน slice เพื่อให้ UI/sequence เทียบได้ */
  detailErrors: string[];
};

export const emptyHistoryV4Slice = (): HistoryV4Slice => ({
  sequenceIndex: null,
  ids: HistoryIdsInitial,
  filter: HistoryFilterInitial,
  encounters: [],
  total: 0,
  offset: 0,
  nextOffset: null,
  selectedId: null,
  detailFor: null,
  detail: null,
  detailErrors: [],
});

export const normalizeHistoryV4Slice = (state: unknown): HistoryV4Slice => {
  const base = emptyHistoryV4Slice();
  if (state === null || typeof state !== "object") return base;

  const data = state as Record<string, unknown>;
  const value = { ...base } as HistoryV4Slice;

  if (data.ids !== undefined && data.ids !== null) value.ids = { ...base.ids, ...(data.ids as HistoryIds) };
  if (data.filter !== undefined && data.filter !== null)
    value.filter = { ...base.filter, ...(data.filter as HistoryFilter) };
  if (Array.isArray(data.encounters)) value.encounters = data.encounters as HistoryEncounterRow[];
  value.offset = typeof data.offset === "number" ? data.offset : 0;
  value.nextOffset = typeof data.nextOffset === "number" ? data.nextOffset : null;
  value.detailLoading = data.detailLoading === true;
  if (typeof data.total === "number") value.total = data.total;
  if (typeof data.selectedId === "number") value.selectedId = data.selectedId;
  if (typeof data.detailFor === "number") value.detailFor = data.detailFor;
  value.detail = data.detail === null || typeof data.detail !== "object" ? null : (data.detail as HistoryDetail);
  if (Array.isArray(data.detailErrors)) value.detailErrors = data.detailErrors as string[];

  return value;
};

/* ============================================================== */
/*   SECTION 2 · วันที่ พ.ศ. (แบบ 4d ใช้ วว/ดด/ปปปป)               */
/* ============================================================== */

/** "15/09/2569" -> 25690915 (ตัวเลขเทียบลำดับแบบเดียว dnum() ในแบบ 4d) */
export const beDateNum = (text: unknown): number | null => {
  const matched = /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:$|\s)/.exec(asText(text).trim());
  if (!matched) return null;
  const day = Number(matched[1]);
  const month = Number(matched[2]);
  const year = Number(matched[3]);
  const date = new Date(year - 543, month - 1, day);
  if (year < 2400 || month < 1 || month > 12 || day < 1 ||
      date.getFullYear() !== year - 543 || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
  return year * 10000 + month * 100 + day;
};

/** 25690915 -> "2026-09-15" (ISO ค.ศ. ที่ backend ใช้ใน params start/end) */
export const beNumToIso = (dateNum: number | null): string | undefined => {
  if (dateNum === null) return undefined;
  const year = Math.floor(dateNum / 10000);
  const month = Math.floor(dateNum / 100) % 100;
  const day = dateNum % 100;
  return `${year - 543}-${`${month}`.padStart(2, "0")}-${`${day}`.padStart(2, "0")}`;
};

/**
 * "15/09/2569 10:12" -> "15/09/2569"
 * this API builds 7 day-columns starting from query `date` (Buddhist era, dd/mm/yyyy);
 * when `date` is missing the backend gets None and raises HTTP 500
 * (NoneType += timedelta) -- so every PHV detail call must carry it. */
export const beDateOnlyText = (row: Pick<HistoryEncounterRow, "dateText" | "dateNum">): string => {
  const matched = /(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(row.dateText || "");
  if (matched) return `${matched[1]}/${matched[2]}/${matched[3]}`;
  if (row.dateNum === null) return "";
  const year = Math.floor(row.dateNum / 10000);
  const month = `${Math.floor(row.dateNum / 100) % 100}`.padStart(2, "0");
  const day = `${row.dateNum % 100}`.padStart(2, "0");
  return `${day}/${month}/${year}`;
};

/** ISO/ข้อความที่ backend ส่ง -> "15/09/2569 10:12" (พ.ศ. แบบ 4d) */
export const beDateTimeText = (value: unknown): string => {
  const raw = asText(value).trim();
  if (raw === "") return "";

  /* backend อาจส่งมาเป็น พ.ศ. อยู่แล้ว (PTM formatted_date, encounter-history `date` = "30/10/2569 [06:18]")
     ตัด [ ] ออกตัวเดียวที่แสดงใน dropdown/ตาราง */
  if (/\d{1,2}\/\d{1,2}\/\d{4}/.test(raw)) return raw.replace(/\s*\[\s*/, " ").replace(/\s*\]/g, "");

  const matched = /(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(raw);
  if (matched) {
    const [, year, month, day, hour, minute] = matched;
    return `${day}/${month}/${Number(year) + 543} ${hour}:${minute}`;
  }

  const dateOnly = /(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (dateOnly) {
    const [, year, month, day] = dateOnly;
    return `${day}/${month}/${Number(year) + 543}`;
  }

  return raw;
};

const beDateNumOf = (value: unknown): number | null => {
  const text = beDateTimeText(value);
  const fromBe = beDateNum(text);
  if (fromBe !== null) return fromBe;

  const iso = /(\d{4})-(\d{2})-(\d{2})/.exec(asText(value));
  if (!iso) return null;
  return (Number(iso[1]) + 543) * 10000 + Number(iso[2]) * 100 + Number(iso[3]);
};

/** ค่า cutoff ของช่วงเวลา 3 เดือน / 1 ปี (คำนวณจาก "วันนี้" ที่ส่งเข้ามาเพื่อให้ test ไม่ผูกกับวันจริง) */
export const periodCutDate = (period: HistoryPeriodKey, todayNum: number): number | null => {
  if (period === "all") return null;

  const year = Math.floor(todayNum / 10000);
  const month = Math.floor(todayNum / 100) % 100;
  const day = todayNum % 100;

  if (period === "1y") return (year - 1) * 10000 + month * 100 + Math.min(day, new Date(year - 544, month, 0).getDate());

  const back = month - 3 <= 0 ? month - 3 + 12 : month - 3;
  const backYear = month - 3 <= 0 ? year - 1 : year;
  /* วันเกินจำนวนวันของเดือนนั้น (เช่น 31 พ.ค. -> 28/29 เม.ย.) ให้ปัดเป็นวันสุดท้ายของเดือน */
  const lastDay = new Date(backYear - 543, back, 0).getDate();
  return backYear * 10000 + back * 100 + Math.min(day, lastDay);
};

/** true เมื่อพิมพ์วันที่ผิดรูปแบบ (แบบ 4d จะยังไม่ใช้ค่าที่พิมพ์ผิดในการกรอง) */
export const hasBadDate = (filter: HistoryFilter): boolean =>
  (filter.from.trim() !== "" && (!/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(filter.from.trim()) || beDateNum(filter.from) === null)) ||
  (filter.to.trim() !== "" && (!/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(filter.to.trim()) || beDateNum(filter.to) === null)) ||
  (beDateNum(filter.from) !== null && beDateNum(filter.to) !== null && beDateNum(filter.from)! > beDateNum(filter.to)!);

export const historyDateError = (filter: HistoryFilter): string =>
  hasBadDate(filter) ? "กรุณาระบุวันที่ที่มีจริง วว/ดด/ปปปป (พ.ศ.) และวันที่เริ่มต้องไม่เกินวันที่สิ้นสุด" : "";

/* ============================================================== */
/*   SECTION 3 · MAP แถว encounter จาก API                        */
/* ============================================================== */

const typeOfRow = (row: Dict): HistoryEncounterType => {
  const raw = asText(row.encounter_type || row.type || row.encounter_type_name || row.ipd_opd).toUpperCase();
  if (raw.includes("IPD") || raw.includes(" Admit".trim().toUpperCase())) return "IPD";
  if (raw.includes("OPD") || raw.includes("OUT")) return "OPD";
  const division = asText(row.division_name || row.division).toUpperCase();
  if (division.includes("IPD") || division.includes("WARD")) return "IPD";
  return raw === "" ? "" : "OPD";
};

const nameOf = (value: unknown): string => {
  /* field ที่เป็น object (division/doctor) ให้เอา name ออกมาก่อน */
  if (value !== null && typeof value === "object" && !Array.isArray(value)) {
    const dict = value as Dict;
    return asText(dict.name || dict.code || dict.full_name);
  }
  if (Array.isArray(value)) return value.map((item) => nameOf(item)).filter(Boolean).join(", ");
  /* id ล้วน (number) ไม่ใช่ชื่อที่แสดง — V4 ใช้ id ใน params doctor/division เท่านั้น */
  if (typeof value === "number") return "";
  return asText(value);
};

const diagnosisTextOf = (row: Dict): string => {
  const direct = row.diagnosis || row.diagnosis_text || row.icd10 || row.icd_code;
  if (direct !== undefined && direct !== null && asText(direct).trim() !== "") {
    if (Array.isArray(direct))
      return direct.map((item) => stripHtml(typeof item === "object" ? asText((item as Dict).icd_term || (item as Dict).name) : item)).filter(Boolean).join(", ");
    return stripHtml(direct);
  }

  const principal = row.principal_diagnosis;
  const secondary = Array.isArray(row.secondary_diagnosis) ? row.secondary_diagnosis : [];
  const texts = [principal, ...secondary]
    .filter(Boolean)
    .map((item) => {
      if (typeof item === "string") return stripHtml(item);
      const dict = item as Dict;
      return [stripHtml(dict.icd_code || dict.icd10_code), stripHtml(dict.icd_term || dict.medical_description)]
        .filter(Boolean)
        .join(" ");
    })
    .filter((text) => text.trim() !== "");

  return texts.join(", ");
};

export function historyEncountersFromApi(payload: unknown): HistoryEncounterRow[] {
  return itemsOf(payload).map((row) => {
    /* /apis/DPO/encounter-history/ answers the day in key `date` ("30/10/2569 [06:18]") and has no
       datetime/created/visit_date. Reading only those keys leaves dateText/dateNum empty, so the
       encounter dropdown shows no day and period filter + sort break. */
    const dateSource =
      row.datetime || row.visit_date || row.created || row.encounter_date || row.date || row.date_iso || row.formatted_date;
    const dateText = beDateTimeText(dateSource);
    const divisionName = nameOf(row.division_name || row.division);
    const doctorName = nameOf(row.doctor_name || row.approved_by_name || row.doctor || row.practitioner_name);
    const diagnosisText = diagnosisTextOf(row);
    const number = asText(row.number || row.encounter_number || row.encounter_no);
    const hn = asText(row.hn || row.patient_hn);
    const id = asNumber(row.id) ?? asNumber(row.encounter) ?? asNumber(row.pk) ?? 0;

    const searchParts = [
      diagnosisText,
      doctorName,
      number,
      hn,
      ...(Array.isArray(row.medical) ? row.medical.map((item) => stripHtml(item.product_name)) : []),
      ...(Array.isArray(row.procedures) ? row.procedures.map((item) => nameOf(item)) : []),
      ...(Array.isArray(row.lab_results) ? row.lab_results.map((item) => nameOf(item)) : []),
    ];

    return {
      id,
      number,
      hn,
      patientName: asText(row.patient_name),
      dateText,
      dateNum: beDateNumOf(dateSource),
      dateIso: asText(dateSource),
      checkoutText: beDateTimeText(row.checkout_datetime || row.checkout || row.depart_datetime || row.discharge_date),
      type: typeOfRow(row),
      divisionName,
      doctorName,
      diagnosisText,
      // EncounterHistoryListSerializer does not provide package usage.
      packageUsed: false,
      emrId: asNumber(row.emr) ?? asNumber(row.medical_record) ?? asNumber(row.emr_id) ?? null,
      isSecret: asBooleanValue(row.is_secret),
      searchBlob: searchParts.filter(Boolean).join(" ").toLowerCase(),
    };
  });
}

const asBooleanValue = (value: unknown): boolean =>
  value === true || asText(value).toLowerCase() === "true";

/* ============================================================== */
/*   SECTION 4 · FILTER / SEARCH (ฝั่ง client ตามแบบ 4d)           */
/* ============================================================== */

export const encounterSearchMatch = (row: HistoryEncounterRow, search: string): boolean => {
  const term = search.trim().toLowerCase();
  if (term === "") return true;
  return row.searchBlob.includes(term) || `${row.diagnosisText} ${row.doctorName}`.toLowerCase().includes(term);
};

/**
 * label ของ <select> Encounter ตามแบบ 4d:
 *   "<วันที่> · OPD (<เลข encounter>) · <diagnosis 4 คำแรก> · 📦"
 */
export const encounterLabel = (row: HistoryEncounterRow): string => {
  const dxWords = (row.isSecret ? "" : row.diagnosisText).split(" ").filter(Boolean).slice(0, 4).join(" ");
  const type = row.type === "IPD" ? "IPD" : "OPD";
  return [
    row.dateText,
    `${type} (${row.number || row.id})`,
    dxWords,
    row.packageUsed ? "📦" : "",
  ]
    .filter((part) => part !== "")
    .join(" · ");
};

export const encounterInfoText = (row: HistoryEncounterRow, divisionFallback = ""): string => {
  const type = row.type === "IPD" ? "IPD" : "OPD";
  return [
    `${type} (${row.number || row.id})`,
    row.dateText,
    row.divisionName || divisionFallback,
    row.doctorName,
    row.isSecret ? "" : row.diagnosisText,
    row.packageUsed ? "Package" : "",
  ]
    .filter((part) => part !== "")
    .join(" · ");
};

export const periodNote = (period: HistoryPeriodKey): string => {
  if (period === "3m") return " (3 เดือนล่าสุด)";
  if (period === "1y") return " (1 ปีล่าสุด)";
  return " (ทั้งหมด)";
};

/**
 * กรองรายการตามตัวกรองของแบบ 4d (ประเภท / ช่วงวันที่ / แผนก / แพทย์ /ช่วงเวลา / คำค้น)
 * `todayNum` คือวันที่ปัจจุบันรูปแบบ พ.ศ. (25691027) — test กำหนดค่าเองได้
 */
export const filterEncounters = (
  rows: HistoryEncounterRow[],
  filter: HistoryFilter,
  todayNum: number,
): HistoryEncounterRow[] => {
  const from = beDateNum(filter.from);
  const to = beDateNum(filter.to);
  const cut = from === null ? periodCutDate(filter.period, todayNum) : null;
  const division = filter.division.trim();
  const doctor = filter.doctor.trim();

  return rows.filter((row) => {
    if (row.type === "IPD" && !filter.ipd) return false;
    if (row.type !== "IPD" && !filter.opd) return false;
    if (division !== "" && division !== "ทั้งหมด") {
      if (!row.divisionName.includes(division) && asText(row.divisionName) !== division) return false;
    }
    if (doctor !== "" && !row.doctorName.includes(doctor)) return false;
    if (from !== null && row.dateNum !== null && row.dateNum < from) return false;
    if (to !== null && row.dateNum !== null && row.dateNum > to) return false;
    if (cut !== null && row.dateNum !== null && row.dateNum < cut) return false;
    return encounterSearchMatch(row, filter.search);
  });
};

/** เรียง newest first (แบบ 4d ใช้ลำดับเก่ากว่า/ใหม่กว่าจาก list ที่กรองแล้ว) */
export const sortEncounters = (rows: HistoryEncounterRow[]): HistoryEncounterRow[] =>
  [...rows].sort((a, b) => (b.dateNum ?? 0) - (a.dateNum ?? 0));

/** params ที่ส่งให้ /apis/DPO/encounter-history/ (V3: CardPHVWrapper.tsx:961-1001) */
export const historyEncounterListParams = (args: {
  ids: HistoryIds;
  filter: HistoryFilter;
  todayNum: number;
  limit?: number;
  offset?: number;
}): Dict => {
  const params: Dict = {};
  if (args.ids.patientId) params.patient = args.ids.patientId;

  /* ช่วงวันที่ของ Search button (type/date range/division/doctor) — client filter ยังใช้ต่อด้วย */
  const from = beDateNum(args.filter.from);
  const to = beDateNum(args.filter.to);
  const beText = (num: number | null): string | undefined => num === null ? undefined :
    `${String(num % 100).padStart(2, "0")}/${String(Math.floor(num / 100) % 100).padStart(2, "0")}/${Math.floor(num / 10000)}`;
  const isoFrom = beText(from ?? periodCutDate(args.filter.period, args.todayNum));
  const isoTo = beText(to);
  if (isoFrom) params.start = isoFrom;
  if (isoTo) params.end = isoTo;

  if (args.filter.division && args.filter.division !== "ทั้งหมด" && args.ids.divisionId) params.division = args.ids.divisionId;
  if (args.ids.doctorId) params.doctor = args.ids.doctorId;

  const { opd, ipd } = args.filter;
  if (opd && !ipd) params.encounter_type = ["OPD", "ER", "OTH", "SS"];
  if (ipd && !opd) params.encounter_type = "IPD";

  params.limit = args.limit ?? HISTORY_PAGE_SIZE;
  params.offset = args.offset ?? 0;
  return params;
};

export const HISTORY_PAGE_SIZE = 200;

/* ============================================================== */
/*   SECTION 5 · encounter ที่ถูกปกปิด (secret encounter)          */
/* ============================================================== */

export function secretEncounterIdsFromApi(payload: unknown): number[] {
  /* SecretEncounter row = request to hide an encounter; only active === true means it is really hidden.
     Staging answers {"id":10555, ..., "active":false} for a normal encounter, so mapping every row to
     secretIds would lock history rows that nothing hides. */
  return itemsOf(payload)
    .filter((row) => row.active === true)
    .map((row) => asNumber(row.encounter) ?? asNumber(row.id) ?? asNumber(row.encounter_id))
    .filter((id): id is number => id !== undefined);
}

/* ============================================================== */
/*   SECTION 6 · MAP รายละเอียด 1 encounter (อ่านอย่างเดียว)        */
/* ============================================================== */

export type HistoryDetailPayloads = {
  encounter: HistoryEncounterRow;
  noEmr?: boolean;
  emr?: unknown;
  diagnosis?: unknown;
  triage?: unknown;
  vitals?: unknown;
  labs?: unknown;
  imaging?: unknown;
  medications?: unknown;
  administration?: unknown;
  procedures?: unknown;
  nurseNotes?: unknown;
  consultNotes?: unknown;
  gallery?: unknown;
  scannedDocs?: unknown;
  certificates?: unknown;
  certificateTypes?: unknown;
  formCertificates?: unknown;
  allergies?: unknown;
  problems?: unknown;
};

const summaryRow = (key: string, value: string, center = false): HistoryRow => ({
  key,
  value: value.trim() === "" ? "-" : value,
  center,
});

/* CentralLabOrderItem.STATUS: a row without a value is pending only while the item has not ended.
   REPORTED rows without their own value exist too (e.g. a panel heading), so they must not read as pending. */
const labNoValueText = (row: { statusName: string; statusLabel?: string }): string => {
  const status = row.statusName.trim().toUpperCase();
  const label = row.statusLabel || row.statusName;
  const suffix = label ? ` (${label})` : "";
  if (status === "REPORTED") return `ไม่มีค่าผลในรายการนี้${suffix}`;
  if (status === "SPECIMEN_REJECTED" || status === "CANCELED") return `ไม่มีผล${suffix}`;
  return status === "" ? "ยังไม่มีผลรายงาน" : `รอผล${suffix}`;
};

const labFlagLetter = (flag: unknown): string => {
  const raw = asText(flag).trim().toUpperCase();
  if (["HH", "LL"].includes(raw)) return raw;
  if (raw === "") return "";
  if (raw.startsWith("H") || raw.includes("HIGH")) return "H";
  if (raw.startsWith("L") || raw.includes("LOW")) return "L";
  return "N";
};

export const labFlagMark = (flag: string): string => flag === "HH" ? " ▲▲ (วิกฤตสูง)" : flag === "LL" ? " ▼▼ (วิกฤตต่ำ)" : (flag === "H" ? " ▲" : flag === "L" ? " ▼" : "");

/** styleของ cell ค่าผลแลป (H = พื้นแดง / L = พื้นเหลือง ตามแบบ 4d) */
export const labFlagStyle = (flag: string): string => {
  if (flag === "HH" || flag === "LL") return "background:#8A1C12;color:#fff;font-weight:bold;";
  if (flag === "H") return "background:#FBE0DD;color:#8A1C12;";
  if (flag === "L") return "background:#FFF1C9;color:#5A3E00;";
  return "";
};

function medicationTexts(payload: unknown): HistoryMedRow[] {
  // DrugOrderItemHistorySerializer: ordered items, including HOME_OPD; MAR is a separate source.
  return itemsOf(payload).map((row) => {
    const quantity = [asText(row.quantity), asText(row.unit_name)].filter(Boolean).join(" ");
    const returned = asText(row.return_quantity);
    return {
      name: stripHtml(row.name),
      howToUse: stripHtml(row.label),
      quantity: [quantity, returned ? `(คืน ${returned})` : ""].filter(Boolean).join(" "),
      /* the API does not exclude cancelled orders, so the status must stay visible next to the drug */
      status: stripHtml(row.order_status_label || row.order_status_name),
    };
  });
}

function administrationTexts(payload: unknown): HistoryMedRow[] {
  return itemsOf(payload).map((row) => {
    const times = Array.isArray(row.admin_time_list) ? row.admin_time_list : Object.values(row.admin_time_dict ?? {});
    const records = times.flatMap((time: unknown) => Object.values(time as Dict))
      .filter((value): value is Dict => value !== null && typeof value === "object");
    return {
      name: stripHtml(row.drug_order_item_summary),
      howToUse: [stripHtml(row.drug_label), asText(row.type_name)].filter(Boolean).join(" · "),
      quantity: records.map((record) => [asText(record.admin_date), asText(record.admin_time), stripHtml(record.summary)]
        .filter(Boolean).join(" ")).filter(Boolean).join("; "),
    };
  });
}

function procedureTexts(payload: unknown): HistoryProcRow[] {
  return itemsOf(payload).map((row) => ({
    name: itemsOf(row.order_items).map((item) => stripHtml(item.product_name)).filter(Boolean).join(", ") || stripHtml(row.order_summary_detail),
    result: [stripHtml(row.result), row.complications ? `ภาวะแทรกซ้อน: ${stripHtml(row.complications)}` : "",
      asText(row.status_name), stripHtml(row.order_perform_by_name), beDateTimeText(row.start), beDateTimeText(row.finish)]
      .filter(Boolean).join(" · "),
  }));
}

function scannedDocTexts(payload: unknown): HistoryFileRow[] {
  return itemsOf(payload).map((row) => ({
    id: asNumber(row.id) ?? 0,
    url: safeHistoryUrl(row.document_image),
    name: asText(row.document_no || row.name || row.file_name || `เอกสาร-${asNumber(row.id) ?? ""}`),
    meta: [
      asText(row.document_type_name || row.document_category_name || row.document_type),
      beDateTimeText(row.document_date),
      nameOf(row.scan_user || row.created_by),
    ]
      .filter((text) => text !== "")
      .join(" · "),
  }));
}

/**
 * MAP payload ทั้งหมดของ 1 encounter -> HistoryDetail
 * (list endpoint ทุกตัวตอบ { items: [...] } -> ต้องเริ่มจาก itemsOf เท่านั้น)
 */
export function historyDetailFromApiPayloads(payloads: HistoryDetailPayloads): HistoryDetail {
  const subjective = subjectiveFromEmr(payloads.emr);
  const diagnosis = diagnosisFromApi(payloads.diagnosis);
  const triage = triageFromApi(payloads.triage);
  const vitals = vitalsFromApi(payloads.vitals);
  const allergies = allergyItemsFromApi(payloads.allergies);
  const problems = problemItemsFromApi(payloads.problems);

  const labRows = labResultsFromApi(payloads.labs).map((row) => ({
    name: row.name,
    value: row.value === "" ? labNoValueText(row) : `${row.value}${labFlagMark(labFlagLetter(row.flagResult))}`.trim(),
    unit: row.unit,
    refValue: row.refValue,
    flag: labFlagLetter(row.flagResult),
  }));

  const imagingRows = imagingResultsFromApi(payloads.imaging).map((row) => ({
    name: row.summary,
    result: row.resultText,
    impression: row.impression || row.doctorSummary,
  }));

  const diagnosisText = [
    diagnosis.principal ? diagnosisLabel(diagnosis.principal) : "",
    ...diagnosis.secondary.map((row) => diagnosisLabel(row)),
  ]
    .filter((text) => text.trim() !== "")
    .join(", ");

  const encounter = payloads.encounter;
  const encounterWithDx: HistoryEncounterRow = {
    ...encounter,
    diagnosisText: encounter.diagnosisText || diagnosisText,
  };

  /* ส่วนที่เรียก API ไม่สำเร็จถูกส่งมาแทนด้วย sentinel PARTIAL_FAILED (ต่างจาก "ไม่มีข้อมูล")
     record ชื่อส่วนไว้ เพื่อให้ UI/sequence แจ้งว่าข้อมูลไม่ครบโดยไม่ hides ทั้งหน้า */
  const partialErrors: string[] = Object.entries(payloads)
    .filter(([key, value]) => key !== "encounter" && value === PARTIAL_FAILED)
    .map(([key]) => key);

  return {
    encounter: encounterWithDx,
    summaryRows: [
      summaryRow(
        "วันที่/เวลาที่เข้าพบแพทย์",
        `Check-in: ${encounterWithDx.dateText} น.${
          encounterWithDx.checkoutText ? `, Checkout: ${encounterWithDx.checkoutText} น.` : ""
        }`,
      ),
      ...vitals.map((chip) => summaryRow(chip.name || chip.code, vitalLabel(chip).replace(`${chip.name} `, ""), true)),
      summaryRow(
        "ประวัติสูบบุหรี่/ประวัติดื่มสุรา",
        [
          `สูบบุหรี่ ${personalTextOf(personalHistoryOf(subjective).smoking)}`,
          `ดื่มสุรา ${personalTextOf(personalHistoryOf(subjective).alcohol)}`,
        ].join("    "),
      ),
      summaryRow(
        "ประวัติการแพ้ (ข้อมูลปัจจุบัน)",
        payloads.allergies === PARTIAL_FAILED ? "ไม่ทราบ (โหลดข้อมูลไม่สำเร็จ)" : allergies.map((item) => [item.name, item.note].filter(Boolean).join(": ")).join(", ") || "ไม่มีข้อมูลประวัติแพ้",
      ),
      summaryRow("รายการยาที่ผู้ป่วยรับประทานอยู่ในปัจจุบัน", "ไม่มีข้อมูลจากแหล่งยาปัจจุบัน"),
      summaryRow("Chief Complaint", subjective.chiefComplaint || triage.chiefComplaint),
      summaryRow("Present illness", subjective.presentIllness),
      summaryRow("Diagnosis", diagnosisText),
      summaryRow("แผนการรักษา", subjective.advice),
    ].map((row) => {
      /* triage is an encounter-level source: it stays valid when the EMR is missing or failed */
      if (row.key === "Chief Complaint" && triage.chiefComplaint) return row;
      const source = ["Chief Complaint", "Present illness", "แผนการรักษา", "ประวัติสูบบุหรี่/ประวัติดื่มสุรา"].includes(row.key) ? "emr" :
        row.key === "Diagnosis" ? "diagnosis" : "";
      return source && partialErrors.includes(source) ? { ...row, value: "โหลดข้อมูลไม่สำเร็จ" } :
        source && payloads.noEmr ? { ...row, value: "ไม่มีเวชระเบียนสำหรับ Encounter นี้" } : row;
    }),
    screeningRows: [
      summaryRow("โรคประจำตัว (ข้อมูลปัจจุบัน)", payloads.problems === PARTIAL_FAILED ? "ไม่ทราบ (โหลดข้อมูลไม่สำเร็จ)" : problems.map((item) => item.message).join(", ") || "ไม่มีข้อมูลโรคประจำตัว"),
      summaryRow(
        "ประวัติการผ่าตัด",
        "ไม่มีข้อมูลจากแหล่งประวัติผ่าตัด",
      ),
      summaryRow(
        "สถานะการมา / ระดับคัดกรอง",
        [triage.arriveStatus ? `สถานะที่มา: ${triage.arriveStatus}` : "", triage.triageLevel ? `Level ${triage.triageLevel}` : ""]
          .filter(Boolean)
          .join(" · "),
      ),
    ],
    labs: labRows,
    imaging: imagingRows,
    meds: medicationTexts(payloads.medications),
    administration: administrationTexts(payloads.administration),
    procs: procedureTexts(payloads.procedures),
    nurseNotes: nurseNotesFromApi(payloads.nurseNotes).map((note) =>
      [beDateTimeText(note.date), nurseNoteText(note)].filter(Boolean).join(" "),
    ),
    consult: consultNotesFromApi(payloads.consultNotes).map((row) => ({
      date: row.date,
      endDate: row.endDate,
      problem: row.problem,
      plan: row.plan,
      goal: row.goal,
      practitioner: row.practitionerName,
      discipline: row.discipline,
    })),
    pictures: galleryFromApi(payloads.gallery).map((photo) => ({
      id: photo.id,
      label: "ภาพบริเวณที่ตรวจ",
      caption: photo.description,
      meta: [
        beDateTimeText(photo.created),

      ]
        .filter(Boolean)
        .join(" · "),
      mark: "🖼",
      url: safeHistoryUrl(photo.image),
    })),
    files: scannedDocTexts(payloads.scannedDocs),
    certificates: [
      ...itemsOf(payloads.certificates).map((row) => ({ id: asNumber(row.id) ?? 0,
        name: asText(row.code) || `ใบรับรองแพทย์ ${asNumber(row.id) ?? ""}`,
        meta: beDateTimeText(row.created), certificate: true })),
      ...medCertsFromLogs(
        itemsOf(payloads.formCertificates),
        medCertTypesFromApi(itemsOf(payloads.certificateTypes)).map((type) => type.code),
      ).map((cert) => ({
        id: cert.id,
        name: `${cert.type} เลขที่ ${cert.no}`,
        meta: [cert.day, cert.doctor].filter(Boolean).join(" · "),
        certificate: true,
        formCertificate: true,
      })),
    ],
    partialErrors,
    noEmr: payloads.noEmr,
  };
}

/** แปลง FormData ของใบรับรองเป็นข้อความที่หน้าประวัติแสดง */
export const formCertificateView = (payload: unknown): Dict => {
  const row = (payload ?? {}) as Dict;
  const data = (row.data ?? {}) as Dict;
  return {
    code: `เลขที่ ${asNumber(row.id) ?? ""}`,
    detail: asText(data.comment),
    diagnosis: asText(data.diagnosis),
    treatment: asText(data.patientAbsenceStart)
      ? `ควรพัก ${asText(data.dayDuration)} วัน ตั้งแต่ ${asText(data.patientAbsenceStart)} ถึง ${asText(data.patientAbsenceEnd)}`
      : "",
  };
};

/** sentinel ว่า "ส่วนนี้เรียกไม่สำเร็จ" (ต่างจาก "ไม่มีข้อมูล") */
export const PARTIAL_FAILED = "__HISTORY_PARTIAL_FAILED__";

const personalHistoryOf = (subjective: { personalHistory: string }): { smoking: string; alcohol: string } => {
  const raw = subjective.personalHistory;
  const smoking = /สูบบุหรี่[: ]*([^;\n]*)/i.exec(raw);
  const alcohol = /สุรา[: ]*([^;\n]*)/i.exec(raw);
  return { smoking: smoking ? smoking[1].trim() : "", alcohol: alcohol ? alcohol[1].trim() : "" };
};

const personalTextOf = (value: string): string => (value.trim() === "" ? "NA" : value.trim());

/* ============================================================== */
/*   SECTION 7 · SECTION LIST (ลำดับหัวข้อตามแบบ 4d)              */
/* ============================================================== */

export const HISTORY_GRIDS = {
  kv: "minmax(150px,300px) 1fr",
  lab: "1.6fr 1fr 0.8fr 1fr",
  imaging: "1.2fr 2fr 2fr",
  medication: "1.6fr 2fr 1fr",
  medicationOrder: "1.6fr 2fr 1fr 1fr",
  procedure: "1.6fr 2fr",
  single: "1fr",
} as const;

export const HISTORY_EMPTY_TEXT = {
  lab: "ไม่มีผลแลปใน Encounter นี้",
  imaging: "ไม่มีผล Imaging ใน Encounter นี้",
  medication: "ไม่มีรายการยาใน Encounter นี้",
  procedure: "ไม่มีหัตถการใน Encounter นี้",
  nurse: "ไม่มีบันทึกพยาบาลใน Encounter นี้",
  scan: "ไม่มีเอกสารสแกนใน Encounter นี้",
  picture: "ไม่มีภาพใน Encounter นี้",
} as const;

const rowsSection = (key: string, title: string, rows: HistoryRow[]): HistorySection => ({
  id: `hs_${key}`,
  key,
  title,
  kind: "rows",
  grid: HISTORY_GRIDS.kv,
  rows,
  columns: [],
  lines: [],
  pictures: [],
  files: [],
  emptyText: "",
});

const tableSection = (
  key: string,
  title: string,
  columns: string[],
  grid: string,
  lines: { cells: string[]; flag: string }[],
  emptyText: string,
): HistorySection => ({
  id: `hs_${key}`,
  key,
  title,
  kind: "table",
  grid,
  rows: [],
  columns,
  lines,
  pictures: [],
  files: [],
  emptyText,
});

/**
 * แถวของตาราง: cell ที่แสดงต้องมีจำนวนตรงกับ columns (แบบ 4d slice ทิ้งค่า flag)
 * flag เก็บไว้คนละช่องเพื่อใช้ตั้งสีของ cell ผลแลปเท่านั้น
 */
const linesOf = (rows: { cells: string[]; flag?: string }[]): { cells: string[]; flag: string }[] =>
  rows.map((row) => ({
    cells: row.cells.map((cell) => (cell.trim() === "" ? "-" : cell)),
    flag: row.flag ?? "",
  }));

/** ลำดับหัวข้อตามแบบ 4d (pic/mc ถูกซ่อนเมื่อไม่มีไฟล์/ภาพ) */
export function buildHistorySections(detail: HistoryDetail): HistorySection[] {
  const medHasStatus = detail.meds.some((row) => row.status);
  const sections: HistorySection[] = [
    rowsSection("sum", "EMR Summary", detail.summaryRows),
    rowsSection("scr", "ข้อมูลคัดกรอง", detail.screeningRows),
  ];

  if (detail.pictures.length > 0) {
    sections.push({
      id: "hs_pic",
      key: "pic",
      title: "ภาพที่บันทึกใน Encounter นี้ (Image Gallery)",
      kind: "pics",
      grid: "",
      rows: [],
      columns: [],
      lines: [],
      pictures: detail.pictures,
      files: [],
      emptyText: HISTORY_EMPTY_TEXT.picture,
    });
  }

  sections.push(
    tableSection(
      "lab",
      "Lab / Blood",
      ["รายการ", "ผล", "หน่วย", "ค่าอ้างอิง"],
      HISTORY_GRIDS.lab,
      linesOf(detail.labs.map((row) => ({ cells: [row.name, row.value, row.unit, row.refValue], flag: row.flag }))),
      HISTORY_EMPTY_TEXT.lab,
    ),
    tableSection(
      "img",
      "Imaging Exam",
      ["รายการ", "Result", "Impression"],
      HISTORY_GRIDS.imaging,
      linesOf(detail.imaging.map((row) => ({ cells: [row.name, row.result, row.impression] }))),
      HISTORY_EMPTY_TEXT.imaging,
    ),
    tableSection(
      "med",
      "รายการสั่งยา",
      medHasStatus ? ["ยา", "วิธีใช้", "จำนวน", "สถานะคำสั่ง"] : ["ยา", "วิธีใช้", "จำนวน"],
      medHasStatus ? HISTORY_GRIDS.medicationOrder : HISTORY_GRIDS.medication,
      linesOf(detail.meds.map((row) => ({
        cells: medHasStatus ? [row.name, row.howToUse, row.quantity, row.status ?? ""] : [row.name, row.howToUse, row.quantity],
      }))),
      HISTORY_EMPTY_TEXT.medication,
    ),
    tableSection(
      "proc",
      "หัตถการ",
      ["รายการ", "ผล / หมายเหตุ"],
      HISTORY_GRIDS.procedure,
      linesOf(detail.procs.map((row) => ({ cells: [row.name, row.result] }))),
      HISTORY_EMPTY_TEXT.procedure,
    ),
  );

  if (detail.administration !== undefined) sections.push(tableSection("mar", "ประวัติการให้ยา (MAR · 7 วันแรกของ Encounter)", ["รายการ", "วิธีใช้ / ประเภท", "บันทึกการให้ยา"],
    HISTORY_GRIDS.medication, linesOf((detail.administration ?? []).map((row) => ({ cells: [row.name, row.howToUse, row.quantity] }))),
    "ไม่มีข้อมูลการให้ยาในช่วง 7 วันแรกของ Encounter นี้"));

  if ((detail.certificates?.length ?? 0) > 0 || detail.partialErrors.includes("certificates")) {
    sections.push({
      id: "hs_mc",
      key: "mc",
      title: "ใบรับรองแพทย์",
      kind: "files",
      grid: "",
      rows: [],
      columns: [],
      lines: [],
      pictures: [],
      files: detail.certificates ?? [],
      emptyText: "",
    });
  }

  sections.push(
    tableSection(
      "nurse",
      "Nurse's record",
      ["บันทึก"],
      HISTORY_GRIDS.single,
      linesOf(detail.nurseNotes.map((note) => ({ cells: [note] }))),
      HISTORY_EMPTY_TEXT.nurse,
    ),
    {
      ...rowsSection("scan", "เอกสารสแกน (รูปภาพ)", []), kind: "files",
      files: detail.files, emptyText: HISTORY_EMPTY_TEXT.scan,
    },
  );

  /* issue 86634 ข้อ 4: Multidisciplinary ในหน้า PHV (แสดงเมื่อ encounterนั้นมีบันทึกทีม) */
  if (detail.consult.length > 0) {
    sections.push(
      tableSection(
        "md",
        "Multidisciplinary",
        ["วันที่เริ่ม", "Problem", "Plan", "Goal", "ผู้บันทึก", "วันที่สิ้นสุด"],
        HISTORY_GRIDS.medicationOrder,
        linesOf(
          detail.consult.map((row) => ({
            cells: [
              row.date,
              row.problem,
              row.plan,
              row.goal,
              [row.practitioner, row.discipline].filter((text) => text !== "").join(" · "),
              row.endDate,
            ],
          })),
        ),
        "ไม่มีบันทึกทีมสหสาขาใน Encounter นี้",
      ),
    );
  }

  const sources: Record<string, string[]> = {
    sum: ["emr", "diagnosis", "vitals", "allergies"], scr: ["triage", "problems"],
    lab: ["labs"], img: ["imaging"], med: ["medications"], mar: ["administration"], proc: ["procedures"],
    nurse: ["nurseNotes"], md: ["consultNotes"], pic: ["gallery"], scan: ["scannedDocs"], mc: ["certificates"],
  };
  if (detail.partialErrors.includes("gallery") && !sections.some((section) => section.key === "pic"))
    sections.push(rowsSection("pic", "ภาพที่บันทึกใน Encounter นี้ (Image Gallery)", []));
  return sections.map((section) => {
    if ((sources[section.key] ?? []).some((source) => detail.partialErrors.includes(source)))
      return { ...section, notice: "โหลดข้อมูลส่วนนี้ไม่สำเร็จ", emptyText: "โหลดข้อมูลส่วนนี้ไม่สำเร็จ" };
    if (detail.noEmr && ["sum", "pic", "mar", "mc"].includes(section.key))
      return { ...section, notice: "ไม่มีเวชระเบียนสำหรับ Encounter นี้" };
    return section;
  });
}

export const sectionNavItems = (sections: HistorySection[]): { id: string; title: string }[] =>
  sections.map((section) => ({ id: section.id, title: section.title }));

/** หา section ที่ flag ของ cell (ผลแลป H/L) ใช้เพื่อไม่ให้ card ต้องเดา style เอง */
export const flagOfLine = (line: { cells: string[]; flag: string }): string => line.flag;

/* ============================================================== */
/*   SECTION 8 · HTML สำหรับพิมพ์ EMR Summary (PDF)               */
/* ============================================================== */

export const escapeHtml = (value: unknown): string =>
  asText(value === undefined || value === null ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");

export function printableEmrSummaryHtml(args: {
  patientName: string;
  hn: string;
  encounter: HistoryEncounterRow;
  sections: HistorySection[];
  printedAt: string;
}): string {
  const { patientName, hn, encounter, sections, printedAt } = args;

  const body = sections
    .map((section) => {
      const title = `<h3>${escapeHtml(section.title)}</h3>${section.notice ? `<p>${escapeHtml(section.notice)}</p>` : ""}`;
      if (section.kind === "rows") {
        const rows = section.rows
          .map((row) => `<tr><th>${escapeHtml(row.key)}</th><td>${escapeHtml(row.value)}</td></tr>`)
          .join("");
        return `${title}<table>${rows}</table>`;
      }
      if (section.kind === "table") {
        if (section.lines.length === 0) return `${title}<p class="mu">${escapeHtml(section.emptyText)}</p>`;
        const head = section.columns.map((column) => `<th>${escapeHtml(column)}</th>`).join("");
        const rows = section.lines
          .map((line) => `<tr>${line.cells.map((cell) => `<td>${escapeHtml(cell)}</td>`).join("")}</tr>`)
          .join("");
        return `${title}<table><tr>${head}</tr>${rows}</table>`;
      }
      if (section.kind === "pics") {
        const tiles = section.pictures
          .map(
            (picture) =>
              `<div class="pic">${picture.url ? `<img style="max-width:100%" src="${escapeHtml(safeHistoryUrl(picture.url))}" alt="${escapeHtml(picture.caption)}">` : ""}<b>${escapeHtml(picture.label)}</b><br>${escapeHtml(
                picture.caption,
              )}<br><span class="mu">${escapeHtml(picture.meta)}</span></div>`,
          )
          .join("");
        return `${title}<div class="pics">${tiles}</div>`;
      }
      const files = section.files
        .map((file) => `<li>${escapeHtml(file.name)} · ${escapeHtml(file.meta)}</li>`)
        .join("");
      return `${title}<ul>${files}</ul>`;
    })
    .join("");

  return [
    '<!doctype html><html lang="th"><head><meta charset="utf-8">',
    `<title>EMR Summary ${escapeHtml(encounter.dateText.slice(0, 10))}</title>`,
    '<style>@page{size:A4;margin:14mm}body{font-family:"Noto Sans Thai","Sarabun",sans-serif;font-size:12px;color:#111}',
    "h1{font-size:18px;margin:0 0 4px}h3{font-size:14px;margin:14px 0 6px;padding:3px 8px;background:#EAF3F6;border-left:4px solid #3FA3BB}",
    "table{width:100%;border-collapse:collapse}th,td{border:1px solid #CBD3D7;padding:4px 6px;text-align:left;vertical-align:top}",
    "th{background:#F3F6F7;width:30%}.mu{color:#555}.pics{display:flex;gap:10px;flex-wrap:wrap}.pic{width:170px;border:1px solid #CBD3D7;padding:6px}",
    '.ph{height:70px;background:#E6ECEE;margin-bottom:4px;text-align:center;line-height:70px}</style></head><body>',
    "<h1>EMR Summary</h1>",
    `<div>${escapeHtml(patientName)} · HN ${escapeHtml(hn)} · Encounter ${escapeHtml(encounter.number || `${encounter.id}`)} · ${escapeHtml(
      encounter.dateText,
    )} น. · ${escapeHtml(encounter.doctorName)}</div>`,
    body,
    `<p class="mu" style="margin-top:16px">พิมพ์เมื่อ ${escapeHtml(printedAt)}</p>`,
    "</body></html>",
  ].join("");
}

/* ============================================================== */
/*   SECTION 9 · helper เล็ก ๆ ที่ card ใช้                        */
/* ============================================================== */

export const historySectionOf = (sections: HistorySection[], key: string): HistorySection | undefined =>
  sections.find((section) => section.key === key);

export const hiddenLabel = (hidden: boolean): string =>
  hidden ? "แสดงประวัติในหน้าจอนี้" : "ซ่อนประวัติในหน้าจอนี้";

export const LOCKED_TEXT =
  '🔒 ประวัติของ Encounter นี้ถูกปกปิดหรือซ่อนในหน้าจอนี้';

export { asNumber, asText };

export const historyPartLabel = (key: string): string => ({
  emr: "เวชระเบียน", diagnosis: "การวินิจฉัย", triage: "คัดกรอง", vitals: "สัญญาณชีพ",
  labs: "ผลแลป", imaging: "ผลรังสี", medications: "รายการยา", procedures: "หัตถการ",
  nurseNotes: "บันทึกพยาบาล", gallery: "ภาพ", scannedDocs: "เอกสารสแกน",
  allergies: "ประวัติแพ้", problems: "โรคประจำตัว", administration: "ประวัติการให้ยา", certificates: "ใบรับรองแพทย์",
} as Record<string, string>)[key] ?? key;

/** File/image URLs are server-provided, but must remain safe when inserted into print HTML or links. */
export const safeHistoryUrl = (value: unknown): string => {
  const url = asText(value).trim();
  return /^(https?:\/\/|\/(?!\/))[^\s]*$/i.test(url) ? url : "";
};

export const printableHistoryImageHtml = (file: HistoryFileRow): string =>
  `<!doctype html><html lang="th"><head><meta charset="utf-8"><title>${escapeHtml(file.name)}</title></head><body><img style="max-width:100%" src="${escapeHtml(safeHistoryUrl(file.url))}" alt="${escapeHtml(file.name)}"></body></html>`;
